import { clamTimeOf } from './scannerLog';
import { counted } from '../composites/countedUnit';

/**
 * 🔒 The malware scanner's health (piece 9; decision 1952; T1280): what clamd
 * answers to VERSION, how many uploads wait for a scan, and the two thresholds
 * past which the nightly run warns. 🔨 Nothing here is stored: the answer is
 * asked when a person or the nightly run asks.
 */

/** 🔒 Hours the scanner may go without answering before the nightly run warns; Settings › Integrations › Malware scanner. */
export const MALWARE_SCANNER_QUIET_HOURS_KEY = 'files.malwareScannerQuietHours';
export const MALWARE_SCANNER_QUIET_HOURS_DEFAULT = 1;

/** 🔒 Days the signature database may age before the nightly run warns and the Status row turns amber. */
export const MALWARE_SIGNATURE_MAX_AGE_DAYS_KEY = 'files.malwareSignatureMaxAgeDays';
export const MALWARE_SIGNATURE_MAX_AGE_DAYS_DEFAULT = 3;

/** The feed entry the nightly run writes to the super admins when the scanner is silent or its signatures are old. */
export const SCANNER_WARNING_ENTRY_TYPE = 'files.scanner.warning';

/** What clamd's VERSION names: the engine, the signature database's version and the day it was built. */
export interface ScannerVersion {
  engine: string | null;
  signatures: string | null;
  /** The instant the signature database was built, as ISO; null where clamd names none. */
  signaturesAt: string | null;
}

/** The health a live check carries beside Answered: the version, the uploads waiting, and whether the signatures are past the threshold. */
export interface ScannerHealth extends ScannerVersion {
  waiting: number;
  signaturesStale: boolean;
  maxAgeDays: number;
}

/**
 * clamd's VERSION answer, `ClamAV 1.4.3/27412/Sun Oct  4 09:24:31 2026`, read
 * into its three parts. A clamd with no database loaded answers the engine
 * alone, and anything else reads as three nulls.
 */
export function clamdVersionOf(reply: string): ScannerVersion {
  const text = reply.replace(/\0+$/, '').trim();
  const found = /^ClamAV ([^/\s]+)(?:\/(\d+)(?:\/(.+))?)?$/.exec(text);
  if (!found) return { engine: null, signatures: null, signaturesAt: null };
  const at = found[3] ? clamTimeOf(found[3]) : null;
  return { engine: found[1]!, signatures: found[2] ?? null, signaturesAt: at ? at.toISOString() : null };
}

/** A threshold read from its Settings row: a positive number, or the default where none is held or the row is not one. */
export function thresholdOf(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

/** 🔒 The signatures are stale past the threshold, and a clamd that names no build date is stale too, since nothing says it updated. */
export function signaturesStale(signaturesAt: string | null, now: Date, maxAgeDays: number): boolean {
  if (!signaturesAt) return true;
  const built = new Date(signaturesAt).getTime();
  return Number.isNaN(built) || now.getTime() - built > maxAgeDays * 86_400_000;
}

/** 🔒 The scanner is quiet when its last answer is older than the threshold, or when it has not answered since the API started. */
export function scannerQuiet(lastAnsweredAt: Date | null, now: Date, quietHours: number): boolean {
  return lastAnsweredAt === null || now.getTime() - lastAnsweredAt.getTime() > quietHours * 3_600_000;
}

/** The day a signature database was built, in words, read in UTC as clamd writes it. */
export function signatureDay(at: string): string {
  const day = new Date(at);
  return Number.isNaN(day.getTime()) ? at : day.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' });
}

/** 🔒 The feed entry's sentence when the scanner has been silent past the threshold (decision 1952). */
export function scannerSilentSummary(where: string, quietHours: number, reason: string): string {
  return `The malware scanner at ${where} has not answered for more than ${counted(quietHours, 'hour')}, so new uploads wait unscanned and cannot be opened. It said: ${reason}`;
}

/** 🔒 The feed entry's sentence when the signature database is older than the threshold (decision 1952). */
export function signaturesOldSummary(where: string, version: ScannerVersion, maxAgeDays: number): string {
  const built = version.signaturesAt ? `are from ${signatureDay(version.signaturesAt)}` : 'name no date';
  return `The malware scanner at ${where} answers, but its signatures ${built}, past the ${counted(maxAgeDays, 'day')} allowed. Its daily update may have stopped; the Scanner Log tab shows what freshclam said.`;
}
